import React, { useEffect, useRef } from 'react';
import * as echarts from 'echarts';
import { CompanyRecommendationType } from '../../types/company.type';

export interface BrokerRatingChartProps {
  companyRecommandation: CompanyRecommendationType | undefined;
  maxNumberOfBrokers?: number;
}

const adjustData = (data: number) => {
  if (data == null) return data;
  if (data <= 3) return 3 + 0.25 * data;
  return data;
};

// Mean rating range [bottom, top] of each stacked segment, in stack order
// (Sell → Strong Buy). Mean scale: 1 = Strong Buy, 5 = Sell.
const SEGMENT_MEAN_RANGES: [number, number][] = [
  [5, 4.5], // Sell
  [4.5, 3.5], // Reduce
  [3.5, 2.5], // Hold
  [2.5, 1.5], // Buy
  [1.5, 1], // Strong Buy
];

// Map a mean rating into the stacked-bar value space of one column so the
// marker lands inside the segment matching its recommendation label.
const mapMeanToStack = (mean: number | null, segments: number[]) => {
  if (mean == null) return null;
  const clamped = Math.min(5, Math.max(1, mean));
  let segIdx = SEGMENT_MEAN_RANGES.findIndex(
    ([bottom, top]) => clamped <= bottom && clamped > top,
  );
  if (segIdx === -1) segIdx = SEGMENT_MEAN_RANGES.length - 1;

  const base = segments
    .slice(0, segIdx)
    .reduce((sum, v) => sum + (v ?? 0), 0);
  const height = segments[segIdx] ?? 0;
  const [bottom, top] = SEGMENT_MEAN_RANGES[segIdx];
  const ratio = (bottom - clamped) / (bottom - top);
  return base + ratio * height;
};

const BrokerRatingChart = ({
  companyRecommandation,
}: BrokerRatingChartProps) => {
  const chartRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    return () => {
      if (chartRef.current) {
        echarts.getInstanceByDom(chartRef.current)?.dispose();
      }
    };
  }, []);

  useEffect(() => {
    if (!chartRef.current) return;

    let chartInstance = echarts.getInstanceByDom(chartRef.current);
    if (!chartInstance) {
      chartInstance = echarts.init(chartRef.current);
    }

    const safeCompanyRecommandation = (companyRecommandation as any) ?? {};
    const _12MAData = safeCompanyRecommandation['12MA'];
    const _2MAData = safeCompanyRecommandation['2MA'];
    const _1MAData = safeCompanyRecommandation['1MA'];
    const _CURRData = safeCompanyRecommandation.CURR;

    // mean <= 0 means no rating (widget defaults missing mean to 0)
    const meanValues = [_12MAData, _2MAData, _1MAData, _CURRData].map((d) => {
      const mean = Number(d?.mean);
      return Number.isFinite(mean) && mean > 0 ? mean : null;
    });
    const rawMeanData = meanValues.map((v) =>
      v != null ? v.toFixed(2) : null,
    );

    let rawData: number[][] = [
      [_12MAData?.sell, _2MAData?.sell, _1MAData?.sell, _CURRData?.sell], // Sell
      [
        _12MAData?.reduce,
        _2MAData?.reduce,
        _1MAData?.reduce,
        _CURRData?.reduce,
      ], // Reduce
      [_12MAData?.hold, _2MAData?.hold, _1MAData?.hold, _CURRData?.hold], // Hold
      [_12MAData?.buy, _2MAData?.buy, _1MAData?.buy, _CURRData?.buy], // Buy
      [
        _12MAData?.strongBuy,
        _2MAData?.strongBuy,
        _1MAData?.strongBuy,
        _CURRData?.strongBuy,
      ], // Strong Buy
    ];

    if (
      !rawData?.length ||
      !rawData[0]?.length ||
      rawData[0].some((v) => v === undefined)
    ) {
      console.warn('BrokerRatingChart: No valid data → showing empty chart');
      chartInstance.clear();
      return;
    }
    const grid = {
      left: 0,
      right: 0,
      top: 0,
      bottom: 50,
    };

    const visualData = rawData.map((row) => row.map(adjustData));

    const containerWidth = chartRef.current.clientWidth;
    const categoryCount = rawData[0].length;
    const barWidthPercent = 0.6;
    const estimatedBarWidth =
      (containerWidth / categoryCount) * barWidthPercent;
    const maxFontForBar = Math.floor(estimatedBarWidth / 5.5);
    const labelFontSize = Math.max(8, Math.min(12, maxFontForBar));

    const barSeries: echarts.SeriesOption[] = [
      'Sell',
      'Reduce',
      'Hold',
      'Buy',
      'Strong Buy',
    ].map((name, sid) => {
      return {
        name,
        type: 'bar',
        stack: 'total',
        barWidth: '60%',
        itemStyle: {
          color: ['#b21b1b', '#E73838', '#7F7F7F', '#34b66b', '#11773c'][sid],
          borderColor: '#fff',
          borderWidth: 1,
        },
        label: {
          show: true,
          fontFamily: "'Arial', 'Helvetica', 'Sans-serif'",
          fontWeight: 'bold',
          fontSize: labelFontSize,
          color: '#fff',
          overflow: 'truncate',
          formatter: (params: any) => {
            const idx = params.dataIndex;
            const rawVal = rawData[params.seriesIndex][idx];
            return rawVal != null ? `${rawVal}` : '';
          },
        },
        data: visualData[sid],
      } as echarts.SeriesOption;
    });

    const meanPositions = meanValues.map((mean, col) =>
      mapMeanToStack(
        mean,
        visualData.map((row) => row[col]),
      ),
    );

    const lineSeries: echarts.SeriesOption = {
      name: 'Average Rating',
      type: 'line',
      data: meanPositions,
      symbol: `image://data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTQiIGhlaWdodD0iMTIiIHZpZXdCb3g9IjAgMCAxNCAxMiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4NCjxwYXRoIGQ9Ik0xMy4wNjI1IDExLjVIMC45Mzc1TDcgMC45OTkwMjNMMTMuMDYyNSAxMS41WiIgZmlsbD0iI0ZGQ0MwMCIgc3Ryb2tlPSJ3aGl0ZSIvPg0KPC9zdmc+DQo=`,
      symbolSize: 12,
      itemStyle: { color: '#FFCC00' },
      lineStyle: { width: 2 },
    };

    const series: echarts.SeriesOption[] = [...barSeries, lineSeries];

    const initialOption: echarts.EChartsOption = {
      legend: { selectedMode: false, show: false },
      grid,
      yAxis: [
        {
          type: 'value',
          axisLine: { show: false },
          axisTick: { show: false },
          splitLine: { show: false },
          axisLabel: { show: false },
        },
      ],
      xAxis: {
        type: 'category',
        data: ['12M\nAgo', '2M\nAgo', '1M\nAgo', 'NOW'],
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { show: true },
      },
      axisLabel: {
        show: true,
        color: '#000',
        fontSize: 14,
        fontFamily: "'Arial', 'Helvetica', 'Sans-serif'",
      },
      tooltip: {
        show: true,
        trigger: 'item',
        axisPointer: { type: 'line' },
        formatter: (params: any) => {
          const idx = params.dataIndex;
          const seriesIdx = params.seriesIndex;
          const meanDisplayData = rawMeanData?.[idx];
          const rawDisplayData = rawData?.[seriesIdx]?.[idx];

          return `
            ${params.seriesName}<br/>
            <span style="display:inline-block;margin-right:4px;border-radius:50%;width:8px;height:8px;background:${params.color};"></span>
            ${params.seriesName === 'Average Rating' ? meanDisplayData : rawDisplayData}
          `;
        },
      },
      series,
    };
    chartInstance.setOption(initialOption as any);

    const updateElements = () => {
      if (!chartInstance) return;
      const gridWidth = chartInstance.getWidth() - grid.left - grid.right;
      const gridHeight = chartInstance.getHeight() - grid.top - grid.bottom;
      const categoryWidth = gridWidth / rawData[0].length;
      const barWidth = categoryWidth * 0.6;
      const barPadding = (categoryWidth - barWidth) / 2;

      const model = (chartInstance as any).getModel();
      const yAxisModel = model.getComponent('yAxis', 0);
      if (!yAxisModel) return;
      const yMax = yAxisModel.axis.scale.getExtent()[1];

      const elements = [];
      const color = ['#b21b1b', '#E73838', '#7F7F7F', '#34b66b', '#11773c'];
      const displayData: number[][] = visualData.map((row) => [...row]);

      for (let j = 1; j < displayData[0].length; ++j) {
        const leftX = grid.left + categoryWidth * j - barPadding;
        const rightX = leftX + barPadding * 2;
        let leftY = grid.top + gridHeight;
        let rightY = leftY;

        for (let i = 0; i < displayData.length; ++i) {
          const points = [];
          const leftVal = displayData[i][j - 1];
          const rightVal = displayData[i][j];

          const leftBarHeight =
            !leftVal || leftVal === 0 ? 0 : (leftVal / yMax) * gridHeight;
          const rightBarHeight =
            !rightVal || rightVal === 0 ? 0 : (rightVal / yMax) * gridHeight;

          points.push([leftX, leftY]);
          points.push([leftX, leftY - leftBarHeight]);
          points.push([rightX, rightY - rightBarHeight]);
          points.push([rightX, rightY]);
          leftY -= leftBarHeight;
          rightY -= rightBarHeight;

          elements.push({
            type: 'polygon',
            shape: { points },
            style: {
              fill: color[i],
              opacity: 0.25,
              stroke: '#fff',
              lineWidth: 2,
            },
          });
        }
      }

      chartInstance.setOption({ graphic: { elements } } as any);
    };

    updateElements();
    let resizeTimer: NodeJS.Timeout;
    const resizeHandler = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        if (chartInstance) {
          chartInstance.resize();
          updateElements();
        }
      }, 150);
    };

    window.addEventListener('resize', resizeHandler);

    return () => {
      clearTimeout(resizeTimer);
      window.removeEventListener('resize', resizeHandler);
    };
  }, [companyRecommandation]);

  return <div ref={chartRef} style={{ width: '100%', height: '250px' }} />;
};

export default BrokerRatingChart;
